import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { authAPI } from "../../services/authAPI";

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = React.useState(0);
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [name, setName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [companyCode, setCompanyCode] = React.useState("");
  const [gstin, setGstin] = React.useState("");
  const [acceptedPrivacy, setAcceptedPrivacy] = React.useState(false);

  const requestOtp = async () => {
    setError("");
    setInfo("");
    if (!acceptedPrivacy) {
      setError("Please accept the Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const data = await authAPI.requestRegisterOtp(email.trim().toLowerCase());
      if (data.debugOtp) {
        setInfo(`OTP sent (dev): ${data.debugOtp}`);
        setOtp(String(data.debugOtp));
      } else {
        setInfo("OTP sent to your email.");
      }
      setStep(1);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error?.message ||
          err.message ||
          "Failed to send OTP"
      );
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async () => {
    setError("");
    if (!acceptedPrivacy) {
      setError("Please accept the Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      await authAPI.registerCompany({
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        password,
        name: name.trim(),
        phone: phone.trim() || undefined,
        companyName: companyName.trim(),
        companyCode: companyCode.trim() || undefined,
        gstin: gstin.trim() || undefined,
      });
      navigate("/login", {
        replace: true,
        state: { message: "Company registered. Please sign in." },
      });
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error?.message ||
          err.message ||
          "Registration failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ bgcolor: "grey.50", p: 2 }}
    >
      <Paper sx={{ p: 4, width: "100%", maxWidth: 520 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Register your company
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Verify email with OTP, then create your company and admin account.
        </Typography>

        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Email OTP</StepLabel>
          </Step>
          <Step>
            <StepLabel>Company</StepLabel>
          </Step>
        </Stepper>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {info ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {info}
          </Alert>
        ) : null}

        {step === 0 ? (
          <Stack spacing={2}>
            <TextField
              label="Work email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{" "}
                  <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener">
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            <Button
              variant="contained"
              disabled={loading || !email.trim() || !acceptedPrivacy}
              onClick={requestOtp}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              Send OTP
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="OTP"
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
            />
            <TextField
              label="Your name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              label="Phone"
              fullWidth
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <TextField
              label="Company name"
              fullWidth
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
            <TextField
              label="Company code (optional)"
              fullWidth
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
            />
            <TextField
              label="GSTIN (optional)"
              fullWidth
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{" "}
                  <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener">
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setStep(0)} disabled={loading}>
                Back
              </Button>
              <Button
                variant="contained"
                disabled={
                  loading ||
                  !acceptedPrivacy ||
                  !otp ||
                  !name.trim() ||
                  !password ||
                  !companyName.trim()
                }
                onClick={submitRegister}
                startIcon={
                  loading ? <CircularProgress size={18} color="inherit" /> : undefined
                }
              >
                Create company
              </Button>
            </Stack>
          </Stack>
        )}

        <Typography variant="body2" sx={{ mt: 3 }}>
          Already have an account?{" "}
          <Link component={RouterLink} to="/login">
            Sign in
          </Link>
          {" · "}
          <Link component={RouterLink} to="/privacy-policy">
            Privacy Policy
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default RegisterPage;
